/* eslint-disable no-console */

/**
 * Sistema de logging controlado para o frontend
 * Permite desabilitar logs em produção via variáveis de ambiente
 */

// Configuração baseada em variáveis de ambiente
const isProduction = process.env.NODE_ENV === 'production';
const debugEnabled = process.env.REACT_APP_DEBUG_LOGS === 'true';
const logLevel = process.env.REACT_APP_LOG_LEVEL || 'INFO';

// Níveis de log
enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  CRITICAL = 4
}

// Mapeamento dos níveis
const levelMap: Record<string, LogLevel> = {
  DEBUG: LogLevel.DEBUG,
  INFO: LogLevel.INFO,
  WARN: LogLevel.WARN,
  ERROR: LogLevel.ERROR,
  CRITICAL: LogLevel.CRITICAL
};

const currentLogLevel = levelMap[logLevel.toUpperCase()] || LogLevel.INFO;

class Logger {
  private name: string;
  private isProduction: boolean;
  private debugEnabled: boolean;
  private currentLevel: LogLevel;

  constructor(name: string = 'AdvogadaParceira') {
    this.name = name;
    this.isProduction = isProduction;
    this.debugEnabled = debugEnabled;
    this.currentLevel = currentLogLevel;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.currentLevel;
  }

  private formatMessage(level: string, message: string, data?: any): string {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${this.name}] [${level}]`;
    
    if (data) {
      return `${prefix} ${message} ${JSON.stringify(data)}`;
    }
    
    return `${prefix} ${message}`;
  }

  /**
   * Log de debug (apenas em desenvolvimento)
   */
  debug(message: string, data?: any): void {
    if (!this.isProduction && this.debugEnabled && this.shouldLog(LogLevel.DEBUG)) {
      console.log(this.formatMessage('DEBUG', message, data));
    }
  }

  /**
   * Log de informação
   */
  info(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(this.formatMessage('INFO', message, data));
    }
  }

  /**
   * Log de aviso
   */
  warn(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage('WARN', message, data));
    }
  }

  /**
   * Log de erro
   */
  error(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(this.formatMessage('ERROR', message, data));
    }
  }

  /**
   * Log crítico (sempre ativo)
   */
  critical(message: string, data?: any): void {
    console.error(this.formatMessage('CRITICAL', message, data));
  }

  /**
   * Logs específicos para funcionalidades
   */
  
  chatDebug(message: string, data?: any): void {
    if (!this.isProduction && this.debugEnabled && this.shouldLog(LogLevel.DEBUG)) {
      console.log(this.formatMessage('CHAT-DEBUG', message, data));
    }
  }

  chatInfo(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(this.formatMessage('CHAT-INFO', message, data));
    }
  }

  apiDebug(message: string, data?: any): void {
    if (!this.isProduction && this.debugEnabled && this.shouldLog(LogLevel.DEBUG)) {
      console.log(this.formatMessage('API-DEBUG', message, data));
    }
  }

  apiInfo(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(this.formatMessage('API-INFO', message, data));
    }
  }

  paymentDebug(message: string, data?: any): void {
    if (!this.isProduction && this.debugEnabled && this.shouldLog(LogLevel.DEBUG)) {
      console.log(this.formatMessage('PAYMENT-DEBUG', message, data));
    }
  }

  paymentInfo(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(this.formatMessage('PAYMENT-INFO', message, data));
    }
  }

  /**
   * Método para capturar e logar erros não tratados
   */
  setupGlobalErrorHandler(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('error', (event) => {
        this.error('Unhandled error:', {
          message: event.error?.message,
          stack: event.error?.stack,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        });
      });

      window.addEventListener('unhandledrejection', (event) => {
        this.error('Unhandled promise rejection:', {
          reason: event.reason,
          promise: event.promise
        });
      });
    }
  }
}

// Instância global do logger
export const logger = new Logger();

// Função para criar loggers específicos
export function createLogger(name: string): Logger {
  return new Logger(name);
}

// Configurar handler global de erros
if (typeof window !== 'undefined') {
  logger.setupGlobalErrorHandler();
}

export default logger; 